import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { records } from '../lib/api';
import toast from 'react-hot-toast';
import { Upload, FileText, X, CheckCircle, Loader } from 'lucide-react';

const DOC_TYPES = ['Resolution','Ordinance','Meeting Minutes','Memorandum','Map','Contract','Report','Other'];
const DEPARTMENTS = ['City Clerk','City Attorney','Information Technology','Public Works','Planning','Finance','Parks & Recreation','Police','Fire','City Manager'];

export default function UploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState('');
  const [form, setForm] = useState({
    title: '', document_type: '', department: '', description: '', record_date: '', tags: ''
  });

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      if (!form.title) setForm(f => ({ ...f, title: accepted[0].name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ') }));
    }
  }, [form.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png','.jpg','.jpeg','.tif','.tiff'], 'application/octet-stream': ['.doc','.docx','.txt'] }
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please select a file');
    if (!form.title || !form.document_type) return toast.error('Title and document type are required');

    setUploading(true);
    setStage('Uploading file...');

    const fd = new FormData();
    fd.append('file', file);
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));

    try {
      setStage('Running OCR — extracting text...');
      const { data } = await records.upload(fd);
      toast.success(`Record uploaded${data.ocr_extracted ? ' with OCR text extracted' : ''}`);
      navigate('/records');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setStage('');
    }
  };

  const fmtSize = (bytes) => bytes < 1e6 ? `${(bytes/1e3).toFixed(0)} KB` : `${(bytes/1e6).toFixed(1)} MB`;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Upload record</h1>
        <p className="text-sm text-gray-500 mt-1">OCR text extraction runs automatically on upload</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Dropzone */}
        <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragActive ? 'border-blue-400 bg-blue-50' : file ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
          <input {...getInputProps()} />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <CheckCircle size={20} className="text-green-500" />
              <div className="text-left">
                <div className="text-sm font-medium text-gray-900">{file.name}</div>
                <div className="text-xs text-gray-500">{fmtSize(file.size)} · {file.type || 'unknown type'}</div>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="ml-4 text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
          ) : (
            <div>
              <Upload size={24} className="mx-auto text-gray-300 mb-3" />
              <div className="text-sm font-medium text-gray-600">{isDragActive ? 'Drop it here' : 'Drag & drop or click to select'}</div>
              <div className="text-xs text-gray-400 mt-1">PDF, PNG, JPG, TIFF, DOC, DOCX, TXT up to 50MB</div>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
          <h2 className="text-sm font-medium text-gray-700">Record metadata</h2>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Title <span className="text-red-400">*</span></label>
            <input value={form.title} onChange={e => set('title', e.target.value)} required
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50"
              placeholder="e.g. Resolution 2024-045 — Budget Amendment" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Document type <span className="text-red-400">*</span></label>
              <select value={form.document_type} onChange={e => set('document_type', e.target.value)} required
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                <option value="">Select type...</option>
                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Department</label>
              <select value={form.department} onChange={e => set('department', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 bg-white">
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Record date</label>
              <input type="date" value={form.record_date} onChange={e => set('record_date', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400"
                placeholder="budget, 2024, amendment" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-blue-400 resize-none"
              placeholder="Brief description of this record..." />
          </div>
        </div>

        {/* OCR notice */}
        <div className="flex items-start gap-3 bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
          <FileText size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium">OCR runs automatically.</span> Full text will be extracted from PDFs and images and indexed for search. Pre-processed documents are accepted without reprocessing.
          </div>
        </div>

        {/* Submit */}
        {uploading && (
          <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 rounded-xl p-4">
            <Loader size={16} className="animate-spin text-blue-500" />
            <span>{stage}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/records')}
            className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={uploading || !file}
            className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {uploading ? 'Processing...' : 'Upload & index record'}
          </button>
        </div>
      </form>
    </div>
  );
}
